/**
 * @file ResetPassword.js — Page de réinitialisation de mot de passe.
 * Affichée lorsque l'utilisateur clique sur le lien reçu par e-mail.
 * Permet de définir un nouveau mot de passe en validant le token (R-0042).
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import { getErrorMessage } from '../lib/errorMessages';
import { APP_ROUTES } from '../lib/routes';

/**
 * Formulaire de réinitialisation avec les paramètres uid et token dans l'URL.
 * Affiche un message de succès après la réinitialisation ou une erreur
 * si le lien est invalide/expiré.
 * @returns {React.ReactElement}
 */
export default function ResetPassword() {
  const { t } = useTranslation();
  const { uid, token } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [visible, setVisible] = useState({ password: false, confirm: false });

  /** Soumet le nouveau mot de passe à l'API avec le token. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError(t('reset_password.passwords_not_match'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm/', { uid, token, password });
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err, t('api.auth.password_reset_invalid_link')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-sm px-6">
        {success ? (
          /* État de succès — mot de passe réinitialisé */
          <div className="text-center">
            <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('reset_password.success_title')}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {t('reset_password.success_message')}
            </p>
            <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={APP_ROUTES.login}>{t('common.sign_in')}</Link>
            </Button>
          </div>
        ) : (
          /* Formulaire de nouveau mot de passe */
          <>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('reset_password.title')}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              {t('reset_password.new_password_hint')}
            </p>
            {error && (
              <div className="mb-4 p-3 text-sm text-red-600 border border-red-200 rounded-sm bg-red-50" data-testid="reset-error">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('reset_password.new_password')}</Label>
                <div className="relative">
                  <Input
                    id="password" type={visible.password ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring pr-10"
                    required minLength={8} data-testid="reset-password-input" autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((current) => ({ ...current, password: !current.password }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="reset-toggle-password"
                    aria-label={visible.password ? t('reset_password.hide_new_password') : t('reset_password.show_new_password')}
                  >
                    {visible.password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('reset_password.confirm_password')}</Label>
                <div className="relative">
                  <Input
                    id="confirm" type={visible.confirm ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring pr-10"
                    required minLength={8} data-testid="reset-confirm-input" autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((current) => ({ ...current, confirm: !current.confirm }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="reset-toggle-confirm"
                    aria-label={visible.confirm ? t('reset_password.hide_confirm_password') : t('reset_password.show_confirm_password')}
                  >
                    {visible.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="reset-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('reset_password.submit')}
              </Button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground text-center">
              <Link to={APP_ROUTES.login} className="text-foreground font-medium hover:underline">{t('forgot_password.back_to_sign_in')}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
